// Pure helpers of the electronic-signature screens (SIGN-FE-01, CdC SIG-01): the boxes and statuses
// with their colours, the progress of a request, the resend countdown, the shortened hash, the draft
// of a new request with its checks and the plain-language error messages. Nothing here renders,
// fetches or keeps a code.
import {
  SIGNATURE_REQUEST_STATUSES,
  type SignatureLevel,
  type SignatureRequest,
  type SignatureRequestStatus,
  type SignatureVerification,
} from '@batiflow/contracts';
import type { CreateSignatureRequestDto } from '@batiflow/api-client';
import { ApiError } from '@batiflow/client-core';
import type { StatusIntent } from '@batiflow/ui';
import type { TFunction } from 'i18next';

export type Translate = TFunction<'signatures'>;

export type { SignatureLevel, SignatureRequest, SignatureRequestStatus, SignatureVerification };
export type Signer = SignatureRequest['signers'][number];

/** Latin digits and codes stay left to right inside an Arabic sentence (LRI … PDI). */
const LRI = String.fromCharCode(0x2066);
const PDI = String.fromCharCode(0x2069);
export const ltr = (text: string): string => `${LRI}${text}${PDI}`;

// --- Boxes and statuses ------------------------------------------------------------------------

export const BOXES = ['to_sign', 'sent'] as const;
export type Box = (typeof BOXES)[number];
export const isBox = (value: unknown): value is Box => BOXES.some((box) => box === value);

export const STATUSES: readonly SignatureRequestStatus[] = SIGNATURE_REQUEST_STATUSES;
export const isStatus = (value: string): value is SignatureRequestStatus =>
  (STATUSES as readonly string[]).includes(value);

/** Colour is never alone: every chip carries the status word (and an icon) too. */
export const STATUS_INTENT: Readonly<Record<SignatureRequestStatus, StatusIntent>> = {
  pending: 'informative',
  completed: 'success',
  declined: 'danger',
  cancelled: 'neutral',
  expired: 'warning',
};

export const SIGNER_INTENT: Readonly<Record<Signer['status'], StatusIntent>> = {
  pending: 'neutral',
  signed: 'success',
  declined: 'danger',
};

/** How many signers have signed, and how many there are. */
export function progressOf(request: Pick<SignatureRequest, 'signers'>): {
  signed: number;
  total: number;
} {
  return {
    signed: request.signers.filter((signer) => signer.status === 'signed').length,
    total: request.signers.length,
  };
}

/** The caller can sign now: the server says so and the request is still open. */
export const canSignNow = (request: SignatureRequest): boolean =>
  request.status === 'pending' && request.me.canSign;

/** The caller made the request and it is still open: the request can be cancelled. */
export const canCancel = (request: SignatureRequest): boolean =>
  request.status === 'pending' && request.me.isRequester;

/** The signer row of the caller, if they are one. */
export const mySigner = (request: SignatureRequest): Signer | undefined =>
  request.signers.find((signer) => signer.id === request.me.signerId);

// --- Codes -------------------------------------------------------------------------------------

/** The platform lets a new code be asked for a minute after the previous one. */
export const RESEND_AFTER_SECONDS = 60;
export const CODE_PATTERN = /^\d{6}$/;
export const isCode = (value: string): boolean => CODE_PATTERN.test(value);

/** Keeps the digits a person typed or pasted ("123 456" → "123456"), at most six. */
export const cleanCode = (value: string): string => value.replace(/\D/g, '').slice(0, 6);

/** Seconds left before a new code may be asked for (0 when it may be asked now). */
export function resendRemaining(sentAtMs: number, nowMs: number): number {
  const elapsed = Math.floor((nowMs - sentAtMs) / 1000);
  return Math.max(0, RESEND_AFTER_SECONDS - Math.max(0, elapsed));
}

/** The error codes after which a new code must be asked for. */
export const needsNewCode = (error: unknown): boolean =>
  apiErrorCode(error) === 'SIGNATURE_CODE_LOCKED' ||
  apiErrorCode(error) === 'SIGNING_BATCH_NOT_PENDING';

// --- Hash and evidence -------------------------------------------------------------------------

/** "9f86d081…0a08" — the start and the end of a SHA-256, enough to recognise it. */
export function shortHash(hash: string): string {
  return hash.length > 20 ? `${hash.slice(0, 8)}…${hash.slice(-8)}` : hash;
}

/** The evidence certificate is polled every few seconds while it is being built, for a minute. */
export const EVIDENCE_POLL_MS = 3000;
export const EVIDENCE_POLL_MAX_MS = 60_000;

export interface VerifyLine {
  id: string;
  /** `ok`: all good; `ko`: a check failed; `unknown`: the platform could not say. */
  state: 'ok' | 'ko' | 'unknown';
  signerId?: string;
}

/**
 * The lines of a verification result, in order: the file, the evidence, then one per qualified
 * signature. A check that does not apply yet (no evidence before completion) is left out.
 */
export function verifyLines(result: SignatureVerification): VerifyLine[] {
  const lines: VerifyLine[] = [{ id: 'document', state: result.documentIntact ? 'ok' : 'ko' }];
  if (result.evidenceIntact !== null)
    lines.push({ id: 'evidence', state: result.evidenceIntact ? 'ok' : 'ko' });
  for (const signature of result.signatures)
    lines.push({
      id: 'signature',
      signerId: signature.signerId,
      state: signature.valid === null ? 'unknown' : signature.valid ? 'ok' : 'ko',
    });
  return lines;
}

// --- Errors ------------------------------------------------------------------------------------

export const apiErrorCode = (error: unknown): string | null =>
  error instanceof ApiError ? error.code : null;
export const isStepUpRequired = (error: unknown): boolean =>
  apiErrorCode(error) === 'STEP_UP_REQUIRED';

/** A plain-language message for an API error: the sentence of its code, or the generic one. */
export function errorMessage(t: Translate, error: unknown): string {
  const code = apiErrorCode(error);
  return code
    ? t(`common:errors.${code}`, { defaultValue: t('common:errors.generic') })
    : t('common:errors.generic');
}

// --- The new request ---------------------------------------------------------------------------

export const MAX_SIGNERS = 10;
export const DEFAULT_VALIDITY_DAYS = 14;
export const MAX_VALIDITY_DAYS = 30;

export interface CreateDraft {
  title: string;
  message: string;
  signerIds: string[];
  level: SignatureLevel;
  /** Typed as text: checked and converted on sending. */
  days: string;
}

export const emptyDraft = (documentTitle: string): CreateDraft => ({
  title: documentTitle,
  message: '',
  signerIds: [],
  level: 'simple',
  days: String(DEFAULT_VALIDITY_DAYS),
});

export interface CreateErrors {
  title?: string;
  signerIds?: string;
  days?: string;
}

export const parseDays = (text: string): number | null => {
  const value = text.trim();
  if (!/^\d{1,2}$/.test(value)) return null;
  const days = Number(value);
  return days >= 1 && days <= MAX_VALIDITY_DAYS ? days : null;
};

export function validateDraft(draft: CreateDraft, t: Translate): CreateErrors {
  const errors: CreateErrors = {};
  if (draft.title.trim() === '') errors.title = t('create.errors.titleRequired');
  if (draft.signerIds.length === 0) errors.signerIds = t('create.errors.signersRequired');
  else if (draft.signerIds.length > MAX_SIGNERS)
    errors.signerIds = t('create.errors.signersTooMany', { max: MAX_SIGNERS });
  if (parseDays(draft.days) === null)
    errors.days = t('create.errors.days', { max: MAX_VALIDITY_DAYS });
  return errors;
}

export const hasErrors = (errors: CreateErrors): boolean => Object.keys(errors).length > 0;

/** The body of the POST: the document's current version, as the API picks it when none is given. */
export function toCreateBody(documentId: string, draft: CreateDraft): CreateSignatureRequestDto {
  const message = draft.message.trim();
  return {
    documentId,
    title: draft.title.trim(),
    ...(message ? { message } : {}),
    level: draft.level,
    signerIds: draft.signerIds,
    expiresInDays: parseDays(draft.days) ?? DEFAULT_VALIDITY_DAYS,
  };
}
